import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {PlayersService} from "../../services/players.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {MatButtonModule} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {
  ColComponent,
  ContainerComponent,
  ListGroupDirective,
  ListGroupItemDirective,
  RowComponent
} from "@coreui/angular";
import {MatSelectModule} from "@angular/material/select";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {PlayerScreenState} from "../../enum/PlayerScreenState";
import {
  ManagePlayerNotesComponent
} from "../../components/manage-player/manage-player-notes/manage-player-notes.component";
import {IGeneralPlayerInfo} from "../../interfaces/player/IGeneralPlayerInfo";
import {
  ManagePlayerTransactionComponent
} from "../../components/manage-player/manage-player-transaction/manage-player-transaction.component";
import {
  ManagePlayerPledgesComponent
} from "../../components/manage-player/manage-player-pledges/manage-player-pledges.component";
import {MatIconModule} from "@angular/material/icon";
import {
  ManagePlayerSuspensionsComponent
} from "../../components/manage-player/manage-player-suspensions/manage-player-suspensions.component";
import {IPlayerSuspension} from "../../interfaces/player/IPlayerSuspension";
import {concatMap, EMPTY, map, Observable, of, Subscription} from "rxjs";
import {PermissionsService} from "../../services/permissions.service";
import {
  ManagePlayerSendGridInfoComponent
} from "../../components/manage-player/manage-player-send-grid-info/manage-player-send-grid-info.component";
import {
  ManagePlayerSentEmailsComponent
} from "../../components/manage-player/manage-player-sent-emails/manage-player-sent-emails.component";
import {UserHasCausableAdminAccessDirective} from "../../directives/permissions/user-has-causable-admin-access.directive";
import {ActiveUserService} from "../../services/active-user.service";
import {
  ManagePlayerGeneralInfoComponent
} from "../../components/manage-player/manage-player-general-info/manage-player-general-info.component";
import {GameService} from "../../services/game.service";
import {AppConfigService} from "../../services/app-config.service";
import {GameThemeType} from "../../enum/GameThemeType";
import {
  ManagePlayerEmailAddressHistoryComponent
} from "../../components/manage-player/manage-player-email-address-history/manage-player-email-address-history.component";
import { MatDialog } from '@angular/material/dialog';
import { DataAccessReasonDialogComponent} from "../../components/dialogs/data-access-reason/data-access-reason.component";
import {DataAccessService} from "../../services/data-access.service";
import {IDataAccessAuthorization} from "../../interfaces/audit/IDataAccessAuthorization";
import {switchMap} from "rxjs/operators";
import {AgGridAngular} from "ag-grid-angular";
import {IGameStats} from "../../interfaces/player/IGameStats";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {PlayerInfoTableDefs} from "../../components/player-game-info/player-info-table-defs";
import {
  ManagePlayerWinningsComponent
} from "../../components/manage-player/manage-player-winnings/manage-player-winnings.component";
import {
  ManagePlayerRefundsComponent
} from "../../components/manage-player/manage-player-refunds/manage-player-refunds.component";
import {RecentWinnerBannerComponent} from "../../components/recent-winner-banner/recent-winner-banner.component";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {
  ManagePlayerGoldRushStripsComponent
} from "../../components/manage-player/manage-player-gold-rush-strips/manage-player-gold-rush-strips.component";
import {
  ManagePlayerFiftyFiftyNumbersComponent
} from "../../components/manage-player/manage-player-fifty-fifty-numbers/manage-player-fifty-fifty-numbers.component";
import {
  ManagePlayerPaymentMethodsComponent
} from "../../components/manage-player/manage-player-payment-methods/manage-player-payment-methods.component";

@Component({
  selector: 'app-manage-game',
  standalone: true,
  imports: [
    ContainerComponent,
    RowComponent,
    ColComponent,
    MatButtonModule,
    HasPermissionDirective,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    ListGroupDirective,
    ListGroupItemDirective,
    ManagePlayerNotesComponent,
    ManagePlayerPledgesComponent,
    ManagePlayerTransactionComponent,
    MatIconModule,
    ManagePlayerSuspensionsComponent,
    ManagePlayerSendGridInfoComponent,
    ManagePlayerSentEmailsComponent,
    UserHasCausableAdminAccessDirective,
    ManagePlayerGeneralInfoComponent,
    ManagePlayerEmailAddressHistoryComponent,
    AgGridAngular,
    ManagePlayerWinningsComponent,
    ManagePlayerRefundsComponent,
    RecentWinnerBannerComponent,
    ManagePlayerGoldRushStripsComponent,
    ManagePlayerFiftyFiftyNumbersComponent,
    ManagePlayerPaymentMethodsComponent
  ],
  templateUrl: './manage-player.component.html',
  styleUrl: './manage-player.component.scss'
})
export class ManagePlayerComponent implements OnInit, OnDestroy {
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private playerService: PlayersService = inject(PlayersService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private permissionService: PermissionsService = inject(PermissionsService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private appConfigService: AppConfigService = inject(AppConfigService);
  private gameService: GameService = inject(GameService);
  private router: Router = inject(Router);
  private dialog: MatDialog = inject(MatDialog);
  private dataAccessService: DataAccessService = inject(DataAccessService);
  private playerInfoTables: PlayerInfoTableDefs = inject(PlayerInfoTableDefs);

  protected readonly PermissionTypes = PermissionTypes;

  public chosenGame: IGameQueryResult | null = null;

  public currentScreenState: PlayerScreenState = PlayerScreenState.GeneralInfo;
  public screenStateEnum = PlayerScreenState;

  public playerInfo: IGeneralPlayerInfo | null = null;
  private playerInfoSubscription: Subscription | null = null;
  public playerId: string | null = '';
  public playerSuspensions: IPlayerSuspension[] = [];
  public playerIsSuspendedInCurrentGame = false;
  public allowUserToChangeGame = false;
  public gameIdFromRoute = '';
  public gameLogo = '';
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;
  public gameThemeType = GameThemeType;
  public gameStatsGridApi!: GridApi<IGameStats>;
  public gameStatsColDefs: ColDef[] = this.playerInfoTables.playerGameStatsColDefs;

  public showRecentWinnerAlert = false;
  public lastWinDate: Date | null = null;
  public contactInfoLockDaysRemaining: number = 0;

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((paramMapP) => {
      this.playerId = paramMapP.get('playerId');
      let gameId = paramMapP.get('gameId');

      if (gameId && this.gameService.activeGame()?.Id == gameId) {
        this.gameIdFromRoute = gameId;
        this.chosenGame = this.gameService.activeGame();
        this.gameLogo = `${this.appConfigService.cdnRoot()}/assets/${this.chosenGame.Subdomain}/game-logo.svg`;
      }

      if (this.playerId) {
        this.getAllPlayerInfo(!gameId);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.playerInfoSubscription) {
      this.playerInfoSubscription.unsubscribe();
    }
  }

  setupFetchSuspensions() {
    // if subject gets value pushed, go get suspensions. This will happen on game change, or if a user adds / resolves a suspension
    this.playerService.fetchUpdatedSuspensionsSubject
      .pipe(concatMap(() => {
          return this.playerService.getPlayerSuspensionsForGame(this.playerInfo?.Id!, this.chosenGame!.Id);
        }
      ))
      .subscribe({
        next: (result) => {
          if (result?.Suspensions) {
            this.playerSuspensions = result.Suspensions;
            this.playerIsSuspendedInCurrentGame = result.PlayerHasActiveSuspension;
          }

        }, error: (err) => {
          this.errorHandlingService.displayPageLevelErrorMessage(err);
        }
      })
  }

  onGameChange(chosenGame: IGameQueryResult) {
    this.uiState = UIStateEnum.ShowLoading;
    this.chosenGame = chosenGame;
    this.gameLogo = `${this.appConfigService.cdnRoot()}/assets/${chosenGame.Subdomain}/game-logo.svg`;

    this.playerService.fetchUpdatedSuspensionsSubject.next(true);
    if (this.permissionService.userHasPermission(PermissionTypes.ViewPlayerSuspensions)) {
      this.setupFetchSuspensions();
    }
  }

  async chooseNewPlayerClick() {
    if (this.gameIdFromRoute) {
      await this.router.navigateByUrl(`/${this.gameIdFromRoute}/player-search`);
    } else {
      await this.router.navigateByUrl(`global-player-search`);
    }
  }

  getAllPlayerInfo(allowChooseGame = false) {
    if (this.appConfigService.privacyPromptsDisabled()) {
      this.fetchPlayerDataDirectly(allowChooseGame);
    } else {
      this.fetchPlayerDataWithDialog(allowChooseGame);
    }
  }

  private fetchPlayerDataDirectly(allowChooseGame: boolean) {
    this.uiState = UIStateEnum.ShowLoading;

    this.playerInfoSubscription = this.fetchPlayerData(null, allowChooseGame).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  private fetchPlayerDataWithDialog(allowChooseGame: boolean) {
    const dialogRef = this.dialog.open(DataAccessReasonDialogComponent, {
      data: { playerId: this.playerId },
      disableClose: true,
      width: '900px'
    });

    this.playerInfoSubscription = dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result) {
          this.uiState = UIStateEnum.ShowLoading;
          return this.fetchPlayerData(result, allowChooseGame);
        } else {
          this.chooseNewPlayerClick();
          return EMPTY;
        }
      })
    ).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  private fetchPlayerData(dialogResult: any | null, allowChooseGame: boolean): Observable<any> {
    return this.playerService.getGeneralPlayerData(this.playerId!, this.activeUserService.activeUser().Id).pipe(
      switchMap(playerData => {
        this.playerInfo = playerData;
        this.handlePlayerGames(allowChooseGame);
        this.setRecentWinnerInfo(playerData);

        if (this.permissionService.userHasPermission(PermissionTypes.ViewPlayerSuspensions) && (!allowChooseGame || this.playerInfo.PlayerGames.length == 1)) {
          this.setupFetchSuspensions();
        }

        // Only do audit logging if dialogResult is provided
        if (dialogResult) {
          const auditData: IDataAccessAuthorization = {
            PlayerId: this.playerId!,
            AccessReason: dialogResult.accessReason,
            AdditionalDetails: dialogResult.additionalDetails || '',
            PlayerName: `${playerData.FirstName} ${playerData.LastName}`
          };

          if (this.gameService.activeGame()) {
            auditData.GameId = this.gameService.activeGame()!.Id;
          }

          return this.dataAccessService.logDataAccessAuthorization(auditData);
        }

        // No audit needed, just return the player data
        return of(playerData);
      })
    );
  }

  refreshPlayerData() {
    this.uiState = UIStateEnum.ShowLoading;

    this.playerService.getGeneralPlayerData(this.playerId!, this.activeUserService.activeUser().Id).subscribe({
      next: (playerData) => {
        this.playerInfo = playerData;
        this.setRecentWinnerInfo(playerData);
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  private handlePlayerGames(allowChooseGame: boolean): void {
    if (!this.playerInfo?.PlayerGames || this.playerInfo.PlayerGames.length <= 0) {
      this.chosenGame = null;
      return;
    }

    if (allowChooseGame) {
      if (this.playerInfo.PlayerGames.length > 1) {
        this.allowUserToChangeGame = true;
      } else if (this.playerInfo.PlayerGames.length === 1) {
        this.chosenGame = this.playerInfo.PlayerGames[0];
        this.gameLogo = `${this.appConfigService.cdnRoot()}/assets/${this.chosenGame.Subdomain}/game-logo.svg`;
        this.uiState = UIStateEnum.ShowLoading;
      }
    }
  }

  onGameStatsReady(params: GridReadyEvent<IGameStats>) {
    this.gameStatsGridApi = params.api;
    this.gameStatsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100
    });
  }

  private setRecentWinnerInfo(playerData: any) {
    this.showRecentWinnerAlert = !!playerData.RecentWinDate;
    this.lastWinDate = playerData.RecentWinDate ? new Date(playerData.RecentWinDate) : null;
    this.contactInfoLockDaysRemaining = playerData.ContactInfoLockDaysRemaining;
  }

  protected readonly GameTypeEnum = GameTypeEnum;
}
